import { useEffect } from "react";
import { get, onDisconnect, ref, remove, serverTimestamp, set } from "firebase/database";
import { rtdb } from "../firebase";
import { GAMES, createRoom, fetchRoomMeta, serverNow } from "./room";

// Public lobbies and Quick match. A room is private unless its host opens it
// (meta.public = true): then the host's page lists it in publicRooms/{code} =
// { game, players, max, at }, and Quick match joins people to a listed lobby.
// A private room can only be joined with its link or its room code, so the
// host closing the lobby is just taking it off the list.

// A listing older than this is ignored: the host's page refreshes it often.
export const LISTING_FRESH_MS = 90_000;
const BEAT_MS = 25_000;
// The game of a lobby that Quick match starts when there's none to join.
export const QUICK_GAME = "doodle";

// How many players a public lobby takes: the top of the game's best range.
export const maxPlayers = (game) => GAMES[game]?.best?.[1] || 8;

// The listed lobbies to try, best first: fresh, with a free place, the fullest
// first (a game can start sooner), and equally full ones in random order.
export function quickMatchOrder(listings, now, rng = Math.random) {
  return Object.entries(listings || {})
    .filter(([, l]) => l && typeof l.at === "number" && now - l.at <= LISTING_FRESH_MS && l.players < l.max && GAMES[l.game] && !GAMES[l.game].casino)
    .map(([code, l]) => ({ code, players: l.players, r: rng() }))
    .sort((a, b) => b.players - a.players || a.r - b.r)
    .map((x) => x.code);
}

// Whether a lobby can still be joined by Quick match (the listing may be a
// little old, so the room itself is checked before joining it).
export const joinable = (meta, uid) => !!meta && meta.public === true && meta.status === "lobby" && !meta.kicked?.[uid];

// Finds a public lobby for `uid` and returns its code. When there's none, a
// new public lobby is started: the next person who presses Quick match joins it.
export async function quickMatch(uid, lang) {
  const listings = (await get(ref(rtdb, "publicRooms"))).val();
  for (const code of quickMatchOrder(listings, serverNow())) {
    const meta = await fetchRoomMeta(code).catch(() => null);
    if (joinable(meta, uid)) return { code, created: false };
  }
  return { code: await createRoom(QUICK_GAME, uid, lang, { public: true }), created: true };
}

// The host's page keeps the lobby listed while it's public and waiting for
// players, and takes it off the list when the host closes it, the game starts,
// or the host's page goes away.
export function usePublicListing(code, active, game, players) {
  useEffect(() => {
    if (!active || !code) return;
    const pRef = ref(rtdb, `publicRooms/${code}`);
    const write = () => set(pRef, { game, players, max: maxPlayers(game), at: serverTimestamp() }).catch(() => {});
    write();
    onDisconnect(pRef).remove().catch(() => {});
    const id = setInterval(write, BEAT_MS);
    return () => {
      clearInterval(id);
      remove(pRef).catch(() => {});
      onDisconnect(pRef).cancel().catch(() => {});
    };
  }, [code, active, game, players]);
}
